<template>
  <ToolShell
    class="rate-calculator"
    tool-name="Retainer pricing calculator"
    h1="Retainer pricing calculator for client work."
    lede="Model a monthly fee from included hours, hourly delivery cost, allocated overhead and a target margin. See the twelve-month scenario and implied selling rate. Free, instant, no signup."
    :trust="['No signup', 'Instant results', 'Cost-based estimate']"
    form-title="Your numbers"
    :upsell-points="[
      'Recurring invoices bill the retainer on schedule, every month',
      'Recurring invoices and automated reminders keep it paid',
      'Retainer revenue is reported per client',
    ]"
    free-label="The free calculator"
    :free-bullets="[
      'The retainer price for one scope, calculated once',
      'You re-issue and chase the invoice every month yourself',
      'No record of what’s been billed or collected per client',
    ]"
    upgrade-headline="This number works once. Workspace369 makes it a workflow."
    :connected-bullets="[
      'Recurring invoices bill the retainer on schedule',
      'Payment links collect online; payment plans start on Specialist',
      'Follow-up reminders run automatically with automations',
      'Retainer revenue is reported per client — status always visible',
    ]"
    slug="/tools/retainer-calculator"
    :meta-description="metaDescription"
    doc-brand="Retainer pricing calculator"
    doc-subtitle="Retainer estimate from your delivery costs"
    doc-kind="SUMMARY"
    stamp="Result"
  >
    <template #form>
      <label class="ta-field">
        <span>Monthly hours included</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M8 4.8V8l2.3 1.6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          <input v-model.number="form.hours" type="number" min="0.01" step="any" placeholder="40" />
        </div>
      </label>

      <label class="ta-field">
        <span>Hourly delivery cost ($)</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5v13M11.5 4.5c-.6-1.2-1.9-1.8-3.5-1.8-1.9 0-3.2.9-3.2 2.4 0 3.4 7 1.7 7 5.1 0 1.6-1.5 2.5-3.8 2.5-1.8 0-3.2-.8-3.8-2" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          <input v-model.number="form.rate" type="number" min="0" step="5" placeholder="100" />
        </div>
      </label>

      <label class="ta-field">
        <span>Overhead allocation (%, optional)</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M12.5 3.5l-9 9" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><circle cx="4.8" cy="4.8" r="1.8" fill="none" stroke="currentColor" stroke-width="1.4"/><circle cx="11.2" cy="11.2" r="1.8" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>
          <input v-model.number="form.overhead" type="number" min="0" step="any" placeholder="15" />
        </div>
      </label>

      <label class="ta-field">
        <span>Target margin (%, optional)</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M12.5 3.5l-9 9" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><circle cx="4.8" cy="4.8" r="1.8" fill="none" stroke="currentColor" stroke-width="1.4"/><circle cx="11.2" cy="11.2" r="1.8" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>
          <input v-model.number="form.margin" type="number" min="0" max="99.99" step="0.01" placeholder="20" />
        </div>
      </label>
      <p class="rate-help">Enter positive monthly hours and a non-negative hourly delivery cost. Blank optional overhead and margin count as zero; margin must be between 0% and 99.99%. The filled values are examples, not market benchmarks.</p>
      <p class="rate-help">Use delivery cost, not an already profitable selling rate. If that cost already includes overhead, leave the additional overhead at zero to avoid counting it twice.</p>
      <p v-show="!result" class="rate-help" role="status">Enter valid hours, delivery cost and allowances to see an estimate. A dash means the estimate is unavailable.</p>
    </template>

    <template #preview>
      <div class="ta-doc-meta">
        <div><span>Monthly hours included</span><strong>{{ number(result?.hours) }}</strong></div>
        <div><span>Hourly delivery cost</span><strong>{{ money(result?.rate) }}</strong></div>
        <div><span>Overhead</span><strong>{{ percent(result?.overhead) }}</strong></div>
        <div><span>Margin</span><strong>{{ percent(result?.margin) }}</strong></div>
      </div>

      <div class="ta-table-scroll" tabindex="0">
        <table class="ta-table">
          <thead>
            <tr><th>Pricing breakdown</th><th>Amount</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Delivery cost before overhead</td>
              <td class="ta-num">{{ money(result?.base) }}</td>
            </tr>
            <tr>
              <td>Allocated overhead amount</td>
              <td class="ta-num">{{ money(result?.overheadAmount) }}</td>
            </tr>
            <tr>
              <td>Modeled margin amount</td>
              <td class="ta-num">{{ money(result?.marginAmount) }}</td>
            </tr>
            <tr>
              <td>Monthly retainer price</td>
              <td class="ta-num">{{ money(result?.monthly) }}</td>
            </tr>
            <tr>
              <td>Twelve-month fee scenario</td>
              <td class="ta-num">{{ money(result?.annual) }}</td>
            </tr>
            <tr>
              <td>Implied hourly selling rate</td>
              <td class="ta-num">{{ money(result?.effectiveHourly) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="ta-doc-total">
        <span>Estimated monthly retainer</span>
        <strong>{{ money(result?.monthly) }}</strong>
      </div>

      <p class="ta-doc-note">The twelve-month scenario assumes twelve identical monthly fees. It does not establish a signed commitment, collected cash, renewal or after-tax profit. Revisit the price when scope, delivery costs or reserved capacity changes, using the review date agreed in the contract.</p>
    </template>

    <template #steps>
      <div><span>01</span><div><h2>Enter the scope</h2><p>The monthly hours included in the retainer and your delivery cost per hour.</p></div></div>
      <div><span>02</span><div><h2>Load overhead and margin</h2><p>Apply any overhead not already in delivery cost, then choose a margin on the modeled monthly fee.</p></div></div>
      <div><span>03</span><div><h2>Read the retainer</h2><p>The estimated fee, twelve-month scenario and implied selling rate update as you type.</p></div></div>
    </template>

    <section class="rate-context">
      <h2>How is a cost-based retainer calculated?</h2>
      <p>Delivery cost = monthly hours × hourly delivery cost. Loaded cost = delivery cost × (1 + overhead percentage ÷ 100). Estimated monthly fee = loaded cost ÷ (1 − target margin ÷ 100). Margin is a share of the final fee, not a markup added to cost.</p>
      <p>For example, 40 hours at a $100 hourly delivery cost gives $4,000. Add 15% overhead to reach $4,600, then divide by 0.8 for a 20% target margin: $5,750 per month. Twelve identical fees total $69,000; the implied selling rate is $143.75 per included hour. These are illustrative inputs, not agency averages.</p>
      <p>Specify included deliverables, revision limits, overage pricing, unused-hours treatment and cancellation terms in the agreement. A fee estimate does not guarantee the costs, utilization, client acceptance or payment timing needed to achieve the modeled margin.</p>
      <p>Use <NuxtLink :to="localePath('/research/agency-pricing-benchmarks')">published agency pricing evidence</NuxtLink> as scope-specific context, not a universal target. Compare <NuxtLink :to="localePath('/blog/fixed-fee-vs-hourly-vs-retainer-billing')">fixed-fee, hourly and retainer billing</NuxtLink>, then <NuxtLink :to="localePath('/blog/how-to-track-agency-profitability')">track actual agency profitability</NuxtLink> against your estimate.</p>
    </section>
  </ToolShell>
</template>

<script setup>
import ToolShell from "~/components/tools/ToolShell.vue";

import { calculateRetainer, planningMoney as money, planningNumber as number, planningPercent as percent } from "~/utils/rate-planning.mjs";
const { localePath } = useWorkspaceLocale();
const form = reactive({ hours: 40, rate: 100, overhead: 15, margin: 20 });
const result = computed(() => calculateRetainer(form));

const metaDescription =
  "Free retainer pricing calculator: estimate a monthly fee from included hours, delivery costs, overhead and target margin. See assumptions and a worked example.";

useHead(() => ({
  title: "Free Retainer Pricing Calculator | Workspace369",
  meta: [
    {
      name: "description",
      content: metaDescription,
    },
    { name: "twitter:title", content: "Free Retainer Pricing Calculator | Workspace369" },
    {
      name: "twitter:description",
      content: "Estimate a monthly retainer from delivery costs, overhead and margin. Compare the twelve-month fee scenario and implied hourly selling rate. Free, no signup.",
    },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: "Free Retainer Pricing Calculator | Workspace369" },
    {
      property: "og:description",
      content: "Estimate a monthly retainer from delivery costs, overhead and margin. Compare the twelve-month fee scenario and implied hourly selling rate. Free, no signup.",
    },
  ],
}));
</script>

<style scoped>
.rate-calculator :deep(.ta-grid > *) { min-width: 0; }
.rate-calculator :deep(.ta-doc-letterhead) { flex-wrap: wrap; }
.rate-calculator :deep(.ta-doc-brandtext) { flex: 1 1 10rem; }
.rate-calculator :deep(.ta-doc-brandtext strong),
.rate-calculator :deep(.ta-doc-brandtext span) { white-space: normal; overflow-wrap: anywhere; }
.rate-calculator :deep(.ta-doc-stamp) { display: none; }
.rate-calculator :deep(.ta-doc-total) { flex-wrap: wrap; }
.rate-calculator :deep(.ta-doc-total span) { flex: 1 1 10rem; }
.rate-calculator :deep(.ta-doc-total strong),
.rate-calculator :deep(.ta-doc-meta > *) { min-width: 0; max-width: 100%; overflow-wrap: anywhere; }
.rate-calculator :deep(.ta-table) { table-layout: fixed; }
.rate-calculator :deep(.ta-table th),
.rate-calculator :deep(.ta-table td) { overflow-wrap: anywhere; white-space: normal; }
.rate-help { color: #475569; font-size: 0.875rem; line-height: 1.6; margin-top: 1rem; }
.rate-context { max-width: 72rem; margin: 0 auto; padding: 3rem 1.25rem; color: #334155; }
.rate-context h2 { font-size: 1.5rem; font-weight: 600; margin-bottom: 1rem; }
.rate-context p { max-width: 70ch; line-height: 1.75; margin-bottom: 1.25rem; }
.rate-context a { text-decoration: underline; text-underline-offset: 0.2em; }

/* Dark mode: the site's dark tokens (page #020617, card #0f172a, hairline #243247, text #f1f5f9 / #cbd5e1 / #94a3b8, accent #67e8f9). Light styles above are unchanged. */
.dark .rate-help {
  color: #cbd5e1;
}

.dark .rate-context {
  color: #f1f5f9;
}


</style>
